import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtDate, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { CheckCircle, FilePdf, ArrowLeft } from "@phosphor-icons/react";

function ym(d) { return d.toISOString().slice(0, 7); }

export default function VatReconciliation() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(ym(new Date()));
  const [data, setData] = useState(null);
  const [reg, setReg] = useState("all");

  const load = useCallback(() => {
    api.get("/reports/vat-reconciliation", { params: { month } }).then((r) => setData(r.data));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => reg === "all" || (r.register || "").toLowerCase() === reg);

  return (
    <Layout>
      <PageHeader
        title="VAT Reconciliation Report"
        subtitle="Transactions by entered month vs the date they were reflected in the filed TRA VAT return"
        actions={
          <>
            <Button variant="ghost" data-testid="back-to-reports" onClick={() => navigate("/reports")}><ArrowLeft size={16} className="mr-1" />Reports</Button>
            <Input type="month" data-testid="recon-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
            <Button variant="outline" data-testid="recon-report-pdf" onClick={() => openPdf(`pdf/vat-reconciliation?month=${month}`)}><FilePdf size={16} className="mr-1" />Download PDF</Button>
          </>
        }
      />
      <div className="p-8">
        {data && (
          <div className="mb-6 grid grid-cols-3 gap-4" data-testid="recon-summary">
            <Card label="Transactions" value={data.count} testid="recon-count" />
            <Card label="Reconciled" value={data.reconciled_count} accent testid="recon-reconciled" />
            <Card label="Unreconciled" value={data.unreconciled_count} testid="recon-unreconciled" />
          </div>
        )}
        <div className="mb-4 flex items-center gap-2">
          {["all", "sales", "purchases"].map((k) => (
            <Button key={k} size="sm" variant={reg === k ? "default" : "outline"} data-testid={`recon-filter-${k}`} onClick={() => setReg(k)} className="capitalize">{k}</Button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white" data-testid="recon-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-3 py-2 text-left">Register</th><th className="px-3 py-2 text-left">Entered</th>
              <th className="px-3 py-2 text-left">Document</th><th className="px-3 py-2 text-left">Party</th>
              <th className="px-3 py-2 text-right">Exclusive</th><th className="px-3 py-2 text-right">VAT</th>
              <th className="px-3 py-2 text-right">Inclusive</th><th className="px-3 py-2 text-left">Return Date</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <TableEmpty colSpan={9} title="Nothing to reconcile" message={`No transactions entered in ${month}.`} />}
              {rows.map((r, i) => (
                <tr key={`${r.register}-${i}`} data-testid={`recon-row-${i}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2"><span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase ${r.register === "Sales" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>{r.register}</span></td>
                  <td className="px-3 py-2 lx-num">{fmtDate(r.entered_date)}</td>
                  <td className="px-3 py-2">{r.document || "—"}</td>
                  <td className="px-3 py-2">{r.party || "—"}</td>
                  <td className="px-3 py-2 text-right lx-num">{fmtNum(r.amount_exclusive, "TZS")}</td>
                  <td className="px-3 py-2 text-right lx-num">{fmtNum(r.vat_amount, "TZS")}</td>
                  <td className="px-3 py-2 text-right lx-num">{fmtNum(r.amount_inclusive, "TZS")}</td>
                  <td className="px-3 py-2 lx-num">{r.vat_return_date ? fmtDate(r.vat_return_date) : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${r.reconciled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.reconciled ? <CheckCircle size={13} weight="fill" /> : null}{r.recon_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Card({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-gray-200 bg-white p-4">
      <div className="lx-label">{label}</div>
      <div className={`mt-2 font-display text-2xl font-bold lx-num ${accent ? "text-emerald-600" : "text-gray-900"}`}>{value ?? 0}</div>
    </div>
  );
}
